import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
  FaSpinner, FaHome, FaSearch, FaFilePdf, FaCheckCircle, FaCopy, FaPlus, FaEdit, FaCircleNotch,
  FaSort, FaSortUp, FaSortDown, FaEnvelope, FaWhatsapp, FaBarcode, FaTrash, FaDownload, FaTools
} from 'react-icons/fa';

import ModalBoleto from "./ModalBoleto.js";
import ConfirmationModal from './ModalConfirmation.js';
import ModalSincronizacao from './ModalSincronizacao.js';
import ModalEditarBoleto from "./ModalEditarBoleto.js";

// Função auxiliar para formatar a data para o formato YYYY-MM-DD
const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const BoletoControlScreen = () => {

  const today = new Date();
  // Define o estado com a data atual formatada
  const [dates, setDates] = useState({
    startDate: formatDate(today),
    endDate: formatDate(today)
  });

  const handleDateChange = (dateType, value) => {
    setDates(prevDates => ({
      ...prevDates,
      [dateType]: value
    }));
  };

  // Filtros
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('Todas');
  const [formaFilter, setFormaFilter] = useState('Todas');
  const [selectedEmitenteId, setSelectedEmitenteId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Editar boleto
  const [openEditModal, setOpenEditModal] = useState(false);
  const [boletoToEdit, setBoletoToEdit] = useState(null);

  const [imprimirBoleto, setImprimirBoleto] = useState('T');

  const handleImprimirChange = (v) => {
    setImprimirBoleto(v);
    localStorage.setItem('imprimir_boleto', v);
  };

  const [selectedId, setSelectedId] = useState(0);

  // Dados
  const [allBoletos, setAllBoletos] = useState([]);
  const [emitentes, setEmitentes] = useState([]);

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [boletosPerPage] = useState(100);
  const [totalPages, setTotalPages] = useState(0);

  // Ordenação
  const [sortKey, setSortKey] = useState('vencimento'); // id | nome | valor | lancamento | vencimento | status
  const [sortDir, setSortDir] = useState('asc'); // asc | desc

  // Seleção em massa
  const [selectedIds, setSelectedIds] = useState(new Set());
  const allCurrentSelected = useMemo(() => selectedIds.size > 0, [selectedIds]);
  const [selectedDelete, setSelectedDelete] = useState(0);

  const [loading, setLoading] = useState(false);
  const [loadingEmitentes, setLoadingEmitentes] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Spinner de segundo plano (widget)
  const [bgVisible, setBgVisible] = useState(false);
  const [bgLabel, setBgLabel] = useState('');
  const [bgTotal, setBgTotal] = useState(0);
  const [bgDone, setBgDone] = useState(0);

  // Acompanhamento de jobs
  const [jobIds, setJobIds] = useState([]);        // ids em acompanhamento
  const [jobStatus, setJobStatus] = useState({});  // mapa id -> status
  const pollingRef = useRef(null);

  const id_layout = 1;
  const token = localStorage.getItem('userToken');
  const URL_BACKEND = process.env.REACT_APP_URL_BACKEND;
  const navigate = useNavigate();

  // Utils
  const currencyBRL = (v) =>
    v != null ? Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '-';

  // Estados do Modal
  const [showModal, setShowModal] = useState(false);
  const [editingBoleto, setEditingBoleto] = useState(null); // null = inclusão

  // Estados do modal de confirmação
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [kindToSend, setKindToSend] = useState(''); // Estado para guardar 'email' ou 'whatsapp'
  const [modalMessage, setModalMessage] = useState('');
  const [currentAction, setCurrentAction] = useState(null); // Pode ser 'resend' ou 'delete'

  // Estado do modal de sincronização
  const [openModalSinc, setOpenModalSinc] = useState(false);

  // Estadps de modal para selecionar layout
  const [showLayoutModal, setShowLayoutModal] = useState(false);
  const [layouts, setLayouts] = useState([]);
  const [layoutSelecionado, setLayoutSelecionado] = useState('');
  const [layoutSinc, setLayoutSinc] = useState('');

  const copyToClipboard = async (text, label = 'Copiado!') => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
    } catch {
      toast.error('Não foi possível copiar.');
    }
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
    setCurrentPage(1);
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <FaSort style={{ opacity: 0.5 }} />;
    return sortDir === 'asc' ? <FaSortUp /> : <FaSortDown />;
  };

  // Preenche emitente e datas padrão (mês corrente)
  useEffect(() => {
    const storedEmitenteId = localStorage.getItem('userIdEmitente');
    if (storedEmitenteId) {
      setSelectedEmitenteId(storedEmitenteId);
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      setStartDate(format(firstDay, 'yyyy-MM-dd'));
      setEndDate(format(lastDay, 'yyyy-MM-dd'));
    } else {
      toast.error('ID do emitente não encontrado. Faça login novamente.');
    }
  }, []);

  // Reidrata jobs do localStorage + consulta ativos no backend
  useEffect(() => {
    if (!URL_BACKEND || !token) return;

    let mounted = true;

    // Restaura IDs salvos
    try {
      const saved = JSON.parse(localStorage.getItem('jobIdsBoletos') || '[]');
      if (Array.isArray(saved) && saved.length) {
        setJobIds(saved);
      }
    } catch { }

    // Consulta jobs ativos no backend
    axios.get(`${URL_BACKEND}/boletos/jobs/ativos`, {
      params: { status: 'pending,processing', id_layout, limit: 500 },
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => {
        if (!mounted) return;
        const ids = r.data?.ids || [];
        const data = r.data?.data || {};
        if (ids.length) {
          setJobIds(ids);
          setJobStatus(data);
          localStorage.setItem('jobIdsBoletos', JSON.stringify(ids));
        }
      })
      .catch(e => console.error('ativos:', e));

    return () => { mounted = false; };
  }, [URL_BACKEND, token]);

  // Polling: acompanha status dos jobs e atualiza progresso
  useEffect(() => {
    // Se não há jobs, encerra tudo e some com o widget
    if (!jobIds.length) {
      setBgVisible(false);
      localStorage.removeItem('jobIdsBoletos');
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }

    // Evita múltiplos intervalos
    if (pollingRef.current || !URL_BACKEND || !token) return;

    pollingRef.current = setInterval(() => {
      const idsSnapshot = [...jobIds]; // evita stale closure

      axios.post(`${URL_BACKEND}/jobs/boletos/status`, { ids: idsSnapshot }, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(r => {
          const data = r.data?.data || {};
          setJobStatus(prev => {
            const merged = { ...prev, ...data };

            // Filtra quem segue ativo
            const stillActive = idsSnapshot.filter(id => {
              const st = merged[id]?.status;
              return st === 'pending' || st === 'processing';
            });

            if (stillActive.length !== jobIds.length) {
              setJobIds(stillActive);
              localStorage.setItem('jobIdsBoletos', JSON.stringify(stillActive));
            }

            // Progresso real (done/error/canceled)
            const finished = idsSnapshot.filter(id => {
              const st = merged[id]?.status;
              return st === 'done' || st === 'error' || st === 'canceled';
            }).length;

            setBgDone(finished);

            // Se terminou tudo, fecha widget e dá um refresh nos boletos
            if (stillActive.length === 0) {
              setTimeout(() => setBgVisible(false), 600);
              localStorage.removeItem('jobIdsBoletos');
              if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
              }
              fetchBoletos(); // reflete PDFs/linhas novas
            }

            return merged;
          });
        })
        .catch(e => console.error('status jobs:', e));
    }, 5000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [jobIds.length, URL_BACKEND, token]); // depende só do length para ligar/desligar

  // Emitentes
  useEffect(() => {
    if (!URL_BACKEND || !token) return;
    const fetchEmitentes = async () => {
      setLoadingEmitentes(true);
      try {
        const { data } = await axios.get(`${URL_BACKEND}/emitentesAll/999/999`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setEmitentes(data || []);
      } catch (error) {
        console.error('Erro ao buscar emitentes:', error);
        toast.error('Erro ao carregar a lista de emitentes.');
      } finally {
        setLoadingEmitentes(false);
      }
    };
    fetchEmitentes();
  }, [URL_BACKEND, token]);

  // Carregar os layouts quando o componente montar
  useEffect(() => {
    const fetchLayouts = async () => {
      try {
        const { data } = await axios.get(`${URL_BACKEND}/config_boletos`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setLayouts(data);
        if (data.length > 0) setLayoutSelecionado(data[0].id); // Default no primeiro
      } catch (e) {
        console.error("Erro ao carregar layouts de boleto", e);
      }
    };
    fetchLayouts();
  }, [token, URL_BACKEND]);

  // Buscar boletos (com filtros)
  const fetchBoletos = async () => {
    if (!selectedEmitenteId || !startDate || !endDate) {
      toast.error('Selecione um emitente e preencha as datas.');
      return;
    }
    if (!URL_BACKEND || !token) return;
    setLoading(true);
    try {
      const urlCompleta = `${URL_BACKEND}/faturamentos/${startDate}/${endDate}/${statusFilter}/${selectedEmitenteId}/${imprimirBoleto}/${formaFilter}`;
      console.log(urlCompleta);

      const { data } = await axios.get(urlCompleta, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllBoletos(Array.isArray(data) ? data : []);
      setCurrentPage(1);
      toast.success(`Boletos carregados com sucesso! (${data?.length || 0} registros)`);
    } catch (error) {
      console.error('Erro ao buscar boletos:', error);
      toast.error(error.response?.data?.message || 'Erro ao carregar boletos.');
      setAllBoletos([]);
    } finally {
      setLoading(false);
      setSelectedIds(new Set());
    }
  };

  // Deriva lista filtrada/ordenada + paginação
  const filteredSorted = useMemo(() => {
    const t = (searchTerm || '').trim().toLowerCase();
    let arr = [...allBoletos];

    // busca local (cliente / id / nossoNumero / linhaDigitavel)
    if (t) {
      arr = arr.filter((b) => {
        const alvo = [
          b?.nome,
          String(b?.id ?? ''),
          b?.nosso_numero || b?.nossoNumero,
          b?.linha_digitavel || b?.linhaDigitavel
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return alvo.includes(t);
      });
    }

    // ordenação
    const getVal = (b) => {
      if (['lancamento', 'vencimento'].includes(sortKey)) {
        const raw = b?.[sortKey];
        if (!raw) return 0;
        try {
          return parseISO(raw)?.getTime?.() || new Date(raw)?.getTime?.() || 0;
        } catch {
          return new Date(raw)?.getTime?.() || 0;
        }
      }
      if (sortKey === 'valor') return Number(b?.valor ?? 0);
      if (sortKey === 'nome') return (b?.nome || '').toLowerCase();
      if (sortKey === 'status') return (b?.status || '').toLowerCase();
      return Number(b?.id ?? 0);
    };

    arr.sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return arr;
  }, [allBoletos, searchTerm, sortKey, sortDir]);

  const paged = useMemo(() => {
    const total = filteredSorted.length;
    const pages = Math.max(1, Math.ceil(total / boletosPerPage));
    setTotalPages(pages);
    const end = currentPage * boletosPerPage;
    const start = end - boletosPerPage;
    return filteredSorted.slice(start, end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredSorted, currentPage, boletosPerPage]);

  // Busca inicial e quando filtros mudam
  useEffect(() => {
    if (selectedEmitenteId && startDate && endDate) {
      fetchBoletos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmitenteId, startDate, endDate, URL_BACKEND, statusFilter]);

  // Paginação
  const goToNextPage = () => currentPage < totalPages && setCurrentPage((p) => p + 1);
  const goToPreviousPage = () => currentPage > 1 && setCurrentPage((p) => p - 1);
  const goToPage = (n) => {
    setCurrentPage(n);
    // opcional: limpa seleção da página anterior
    const idsPage = paged.map(b => b.id);
    setSelectedIds(prev => {
      const nset = new Set(prev);
      idsPage.forEach(id => nset.delete(id));
      return nset;
    });
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const max = 5;
    let start = Math.max(1, currentPage - Math.floor(max / 2));
    let end = Math.min(totalPages, start + max - 1);
    if (end - start + 1 < max) start = Math.max(1, end - max + 1);
    for (let i = start; i <= end; i++) pageNumbers.push(i);

    return (
      <ul style={ui.paginationList}>
        {start > 1 && (
          <>
            <li style={ui.paginationItem} onClick={() => goToPage(1)}>
              <button style={ui.paginationButton}>1</button>
            </li>
            {start > 2 && <li style={ui.paginationEllipsis}>…</li>}
          </>
        )}
        {pageNumbers.map((n) => (
          <li key={n} style={ui.paginationItem} onClick={() => goToPage(n)}>
            <button
              style={{ ...ui.paginationButton, ...(currentPage === n ? ui.paginationButtonActive : {}) }}
            >
              {n}
            </button>
          </li>
        ))}
        {end < totalPages && (
          <>
            {end < totalPages - 1 && <li style={ui.paginationEllipsis}>…</li>}
            <li style={ui.paginationItem} onClick={() => goToPage(totalPages)}>
              <button style={ui.paginationButton}>{totalPages}</button>
            </li>
          </>
        )}
      </ul>
    );
  };

  const handleOpenBoletoPdf = async (boletoId, nome_arquivo, linha_digitavel) => {
    try {
      if (nome_arquivo.toLowerCase() !== 'x') {
        const win = window.open('', '_blank');
        const resp = await axios.get(`${URL_BACKEND}/imprimirBoleto/${boletoId}/pdf`, {
          responseType: 'blob',
          headers: { Authorization: `Bearer ${localStorage.getItem('userToken')}` }
        });
        const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
        if (win) win.location = url;
        else window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } else {
        // Se o arquivo não existe, manda gerar
        const resp = await axios.get(`${URL_BACKEND}/boleto/${boletoId}/pdf`, {
          responseType: 'arraybuffer' // Mantenha como arraybuffer para tratar binários
        });

        // Se a requisição for bem-sucedida, trate a resposta como PDF
        const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
        const win = window.open(url, '_blank');
        if (!win) {
          toast.error('O navegador bloqueou a abertura do PDF.');
        }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
        toast.success('Boleto gerado com sucesso!');
      }
    } catch (e) {
      console.error('Erro na requisição:', e);

      // Tente decodificar a resposta de erro
      if (e.response && e.response.data) {
        try {
          // Tenta decodificar a resposta como texto e parsear o JSON
          const decoder = new TextDecoder('utf-8');
          const erroData = decoder.decode(e.response.data);
          const erroObj = JSON.parse(erroData);

          // Exibe a mensagem de erro que veio do backend
          toast.error(erroObj.error || 'Erro desconhecido na geração do boleto.');
        } catch (parseError) {
          // Se o erro não for um JSON, mostra uma mensagem genérica
          console.error('Erro ao decodificar a resposta de erro:', parseError);
          toast.error('Erro inesperado na comunicação com o servidor. Por favor, tente novamente.');
        }
      } else {
        // Caso não haja resposta ou dados
        toast.error('Não foi possível se conectar ao servidor.');
      }
    }
  };

  // Baixa manual - abrir modal de confirmação
  const handleBaixaManual = async (boletoId) => {
    setModalMessage('Confirma a baixa desse registro?');
    setSelectedId(boletoId);
    setCurrentAction('baixa');
    setIsModalOpen(true);
  }

  // Baixa manual - abrir modal de confirmação
  const handleBaixaManualLote = async () => {
    setModalMessage('Confirma a baixa de todos os selecionados?');
    setCurrentAction('baixa_lote');
    setIsModalOpen(true);
  }

  // Baixa manual (individual ou em lote)
  const handleMarkAsPaid = async (cancelado) => {
    // if (!window.confirm(`Tem certeza que deseja dar baixa manual no boleto de ID ${boletoId}?`)) return;

    if (!URL_BACKEND || !token) return;
    if (!selectedIds) return;
    console.log('to aqui');
    if (currentAction === 'baixa_lote') {
      try {
        setLoading(true);
        // Converta o Set para um Array
        const idsEmArray = Array.from(selectedIds);
        const obj = {
          cancelado,
          ids: idsEmArray // Envia o Array, não o Set
        };
        const retorno = await axios.put(`${URL_BACKEND}/boletos/dar-baixa-lote`, obj, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Retorno: ' + retorno);

      } catch (error) {
        console.log(error);
        alert(error);
      } finally {
        setLoading(false);
        fetchBoletos();
      }
    } else {
      try {
        const retorno = await axios.put(`${URL_BACKEND}/boletos/dar-baixa/${selectedId}`, { cancelado }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Baixando: ' + JSON.stringify(retorno));
        if (retorno.data && retorno.data.success) {
          toast.success(`Boleto ${selectedId} baixado com sucesso!`, { id: 'baixaBoleto' });
        } else {
          toast.error(retorno.message);
        }
        fetchBoletos();
      } catch (error) {
        console.error('Erro ao dar baixa no boleto:', error);
        toast.error(error.response?.data?.message || `Erro ao dar baixa no boleto ${selectedId}.`, {
          id: 'baixaBoleto'
        });
      }
    }
  };

  // Abre o modal de confirmação para excluir
  const handleDeleteClick = (recordId) => {
    setModalMessage('Confirma a exclusão deste registro?');
    setCurrentAction('delete');
    // Você pode armazenar o ID do registro a ser excluído se precisar
    setSelectedDelete(recordId.id);
    setIsModalOpen(true);
  };

  // Abre o modal de confirmação para enviar email ou whats
  const handleOpenConfirmation = (kind) => {
    if (selectedIds.size === 0) {
      return toast.error('Selecione pelo menos 1 boleto.');
    }
    setCurrentAction('resend');
    if (kind === 'email') {
      setModalMessage('Confirma o reenvio do email')
    }
    if (kind === 'whatsapp') {
      setModalMessage('Confirma o reenvio do whatsapp')
    }
    setKindToSend(kind);
    setIsModalOpen(true);
  };

  const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const handleConfirm = (cancelado) => {
    if (currentAction === 'resend') {
      handleBulkResend();
    } else if (currentAction === 'delete') {
      // Chame a função de exclusão      
      handleDeleteConfirm();
    } else if (currentAction === 'baixa') {
      handleMarkAsPaid(cancelado)
    } else if (currentAction === 'baixa_lote') {
      handleMarkAsPaid(cancelado)
    }
    setIsModalOpen(false);
  };

  const handleDeleteConfirm = async () => {
    // Lógica para exclusão do registro
    if (!URL_BACKEND || !token) return;
    toast.loading('Excluindo registro...' + selectedDelete);
    try {
      await axios.delete(`${URL_BACKEND}/faturamento/${selectedDelete}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Registro excluído!');
      fetchBoletos();
    } catch (e) {
      toast.error('Falha ao excluir o registro.');
    }
  };

  // Reenvios da parte de cima da tela, reenvio em massa
  const handleBulkResend = async () => {
    if (!URL_BACKEND || !token) return;

    setIsModalOpen(false); // Fecha o modal
    setLoading(true); // Ativa o loading

    const label = kindToSend === 'email' ? 'e-mails' : 'WhatsApp';
    const toastId = `bulk-${kindToSend}`;
    toast.loading(`Reenviando ${label}...`, { id: toastId });

    try {
      // Adiciona uma lista para rastrear falhas
      const failedIds = [];

      // Loop sequencial com delay
      for (const id of selectedIds) {
        try {
          let obj = { boletoId: id };
          if (label === 'WhatsApp') {
            await axios.post(`${URL_BACKEND}/boleto/reenviar-whatsapp`, obj, {
              headers: { Authorization: `Bearer ${token}` }
            });
            await sleep(5000);
          } else {
            const obj = { id };
            await axios.post(`${URL_BACKEND}/boletos/reenviar-email`, obj, {
              headers: { Authorization: `Bearer ${token}` }
            });
            await sleep(2000);
          }
        } catch (e) {
          console.error(`Falha ao reenviar o boleto ${id}:`, e);
          failedIds.push(id); // Adiciona o ID falho na lista
        }
      }

      if (failedIds.length > 0) {
        toast.error(`Falha em ${failedIds.length} reenvios.`, { id: toastId });
      } else {
        toast.success('Reenvios concluídos!', { id: toastId });
      }

    } catch (e) {
      // Este catch externo só será ativado se houver um erro antes do loop
      console.error(e);
      toast.error('Ocorreu um erro inesperado.', { id: toastId });

    } finally {
      setLoading(false); // Desativa o loading, independente do resultado
    }
  };

  // Envio de email um a um
  const handleResendEmail = async (boletoId, st) => {
    if (!URL_BACKEND || !token) return;
    toast.loading('Reenviando por e-mail...', { id: `reemail-${boletoId}` });
    try {
      const obj = { id: `'${boletoId}'`, tipo: `'${st}'` }
      await axios.post(`${URL_BACKEND}/boletos/reenviar-email`, obj, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('E-mail reenviado!', { id: `reemail-${boletoId}` });
    } catch (e) {
      toast.error('Falha ao reenviar e-mail.', { id: `reemail-${boletoId}` });
    }
  };

  // Envio de whatsapp um a um
  const handleResendWhatsapp = async (boletoId) => {
    if (!URL_BACKEND || !token) return;
    toast.loading('Reenviando no WhatsApp...', { id: `rewhats-${boletoId}` });
    try {
      await axios.post(`${URL_BACKEND}/boleto/reenviar-whatsapp`, { boletoId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('WhatsApp reenviado!', { id: `rewhats-${boletoId}` });
    } catch (e) {
      toast.error('Falha ao reenviar WhatsApp.', { id: `rewhats-${boletoId}` });
    }
  };

  // Ações em massa
  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const toggleSelectAllCurrent = () => {
    const idsPage = paged.map((b) => b.id);
    setSelectedIds((prev) => {
      const n = new Set(prev);
      const allSelected = idsPage.every((id) => n.has(id));
      if (allSelected) idsPage.forEach((id) => n.delete(id));
      else idsPage.forEach((id) => n.add(id));
      return n;
    });
  };

  const handleCloseModalSinc = () => {
    setOpenModalSinc(false);
  };

  // Sincronizar com o banco e dar baixa automatica
  const handleConfirmSinc = async () => {
    if (!layoutSinc) return toast.error("Selecione um banco para sincronizar.");
    setIsLoading(true); // Inicia o carregamento

    try {
      const body = { idLayout: layoutSinc, dataI: dates.startDate, dataF: dates.endDate };

      // AQUI: Usar await para esperar a resposta da API
      await axios.post(`${URL_BACKEND}/sincronizarBoleto`, body, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Estas linhas só serão executadas após a requisição ser concluída com sucesso
      toast.success('Sincronização concluída com sucesso');
      handleCloseModalSinc(); // Fecha o modal

    } catch (error) {
      console.error('Erro na sincronização:', error);
      // As mensagens de erro geralmente estão em 'response.data.message'
      const errorMessage = error.response?.data?.message || 'Falha ao sincronizar.';
      toast.error(errorMessage);

    } finally {
      // AQUI: O loading deve ser desativado após o término da operação
      setIsLoading(false);
    }
  };

  // Alterar vencimento na aplicacao e no banco
  const handleAlteraVencimento = async (data) => {
    const hoje = new Date().toISOString().split("T")[0];
    if (data.vencimento < hoje) {
      toast("Atenção: vencimento próximo!", {
        style: {
          background: "#fef3c7",
          color: "#92400e",
        },
        icon: "⚠️",
      });
      return;
    }

    if (data.abatimento <= 0) {
      // Alterar o vencimento do boleto
      const obj = {
        novoVencimento: data?.vencimento,
        numeroBoleto: data.nossonumero,
        idFat: data.id,
        idLayout: data.id_layout_boleto
      }
      const retorno = await axios.put(`${URL_BACKEND}/boleto/altVencto`, obj);
      if (retorno.data.success) {
        toast.success(retorno.data.message);
        fetchBoletos(); // recarrega a lista
      } else {
        toast.error(retorno.data.message);
      }
    } else {
      // Registrar abatimento no boleto
      const obj = {
        idFat: data.id,
        idLayout: data.id_layout_boleto,
        valor: data.abatimento
      }
      const retorno = await axios.put(`${URL_BACKEND}/boleto/abatimento`, obj);
      if (retorno.data.success) {
        toast.success(retorno.data.message);
        fetchBoletos(); // recarrega a lista
      } else {
        toast.error(retorno.data.message);
      }
    }
    setOpenEditModal(false);
  }

  // Abrir modal de sincronizacao com o banco
  const handleOpenSincronizar = () => {
    setOpenModalSinc(true);
  }

  // Geração de boletos: enfileira e deixa o polling acompanhar
  // const gerarBoletos = (kind = 'gerar') => {
  //   const ids = Array.from(selectedIds);
  //   if (!ids.length) return toast.error('Selecione pelo menos 1 boleto.');
  //   if (!window.confirm(`Gerar boletos de ${ids.length} selecionados?`)) return;
  //   if (!URL_BACKEND || !token) return toast.error('Sessão inválida. Faça login novamente.');

  //   // widget
  //   setBgLabel('Gerando boletos');
  //   setBgTotal(ids.length);
  //   setBgDone(0);
  //   setBgVisible(true);

  //   // limpar seleção imediatamente
  //   setSelectedIds(new Set());

  //   const toastId = `bulk-${kind}-${Date.now()}`;
  //   toast.loading('Gerando boletos em segundo plano…', { id: toastId });

  //   const body = { id_layout, ids };
  //   axios.post(`${URL_BACKEND}/jobs/boletos/enfileirar`, body, {
  //     headers: { Authorization: `Bearer ${token}` }
  //   })
  //     .then(r => {
  //       const returnedIds = r.data?.ids || r.data?.jobIds || ids;
  //       setJobIds(returnedIds);
  //       localStorage.setItem('jobIdsBoletos', JSON.stringify(returnedIds));
  //       setBgTotal(returnedIds.length);
  //       toast.success('Jobs de geração enfileirados!', { id: toastId });
  //     })
  //     .catch(e => {
  //       console.error(e);
  //       toast.error('Falha ao enfileirar geração.', { id: toastId });
  //       setBgVisible(false);
  //     });
  // };

  // 1. O botão da tela chama esta função:
  const gerarBoletos = (kind = 'gerar') => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return toast.error('Selecione pelo menos 1 boleto.');

    // Em vez de window.confirm, abrimos nosso Modal customizado
    setShowLayoutModal(true);
  };

  // 2. Esta função é chamada pelo botão "Confirmar" dentro do Modal:
  const confirmarGeracaoFinal = () => {
    const ids = Array.from(selectedIds);
    if (!URL_BACKEND || !token) return toast.error('Sessão inválida.');

    // widget
    setBgLabel('Gerando boletos');
    setBgTotal(ids.length);
    setBgDone(0);
    setBgVisible(true);

    setSelectedIds(new Set());

    const toastId = `bulk-gerar-${Date.now()}`;
    toast.loading('Gerando boletos em segundo plano…', { id: toastId });

    // USAMOS O ID DO LAYOUT SELECIONADO NO MODAL
    const body = { id_layout: layoutSelecionado, ids };

    axios.post(`${URL_BACKEND}/jobs/boletos/enfileirar`, body, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => {
        const returnedIds = r.data?.ids || r.data?.jobIds || ids;
        setJobIds(returnedIds);
        localStorage.setItem('jobIdsBoletos', JSON.stringify(returnedIds));
        setBgTotal(returnedIds.length);
        toast.success('Jobs de geração enfileirados!', { id: toastId });
      })
      .catch(e => {
        console.error(e);
        toast.error('Falha ao enfileirar geração.', { id: toastId });
        setBgVisible(false);
      });
  };




  // Resumo
  const resumo = useMemo(() => {
    const total = allBoletos.length;
    const pagos = allBoletos.filter((b) => b.status === 'Pago').length;
    const vencidos = allBoletos.filter((b) => b.status === 'Vencido').length;
    const abertos = total - pagos - vencidos;
    const soma = allBoletos.reduce((acc, b) => acc + Number(b?.valor ?? 0), 0);
    return { total, pagos, vencidos, abertos, soma };
  }, [allBoletos]);

  const statusBadgeStyle = (status) => {
    if (status === 'Pago') return ui.badgeGreen;
    if (status === 'Vencido') return ui.badgeRed;
    if (status === 'Cancelado') return ui.badgeGray;
    return ui.badgeYellow; // Aberto / Outras
  };

  // Baixar PDFs selecionados em um ZIP
  const downloadSelecionados = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return toast.error('Selecione ao menos 1.');

    const toastId = 'zip';
    toast.loading('Preparando ZIP…', { id: toastId });

    try {
      const resp = await axios.post(
        `${URL_BACKEND}/boletos/download-zip`,
        { ids },
        {
          responseType: 'blob',
          headers: { Authorization: `Bearer ${token}` },
          onDownloadProgress: (e) => {
            if (e.total) {
              const pct = Math.round((e.loaded / e.total) * 100);
              toast.loading(`Baixando… ${pct}%`, { id: toastId });
            }
          }
        }
      );

      // Se o backend retornar erro (JSON), evita salvar um "zip" inválido
      const ct = (resp.headers['content-type'] || '').toLowerCase();
      if (!ct.includes('application/zip')) {
        const text = await resp.data.text().catch(() => '');
        try {
          const { error, message } = JSON.parse(text || '{}');
          toast.error(error || message || 'Falha ao gerar ZIP', { id: toastId });
        } catch {
          toast.error(text || 'Falha ao gerar ZIP', { id: toastId });
        }
        return;
      }

      // Pega o nome do arquivo do header, se vier
      const dispo = resp.headers['content-disposition'] || '';
      const m = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(dispo);
      const fileName =
        (m && decodeURIComponent(m[1].replace(/"/g, ''))) ||
        `boletos_${new Date().toISOString().slice(0, 10)}.zip`;

      const blob = new Blob([resp.data], { type: 'application/zip' });
      const href = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = href;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();

      setTimeout(() => URL.revokeObjectURL(href), 60_000);
      toast.success('ZIP baixado!', { id: toastId });
    } catch (err) {
      console.error(err);
      toast.error('Erro ao baixar ZIP.', { id: toastId });
    }
  };

  // Imprimir todos os selecionados
  const imprimirSelecionados = async () => {
    try {
      const ids = Array.from(selectedIds);
      if (!ids.length) return toast.error('Selecione ao menos 1.');
      setLoading(true);
      const url = `${URL_BACKEND}/boletos/merge-pdf?ids=${ids.join(',')}`;

      const resp = await axios.get(url, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` }
      });
      const blobUrl = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const win = window.open(blobUrl, '_blank');
      if (!win) return toast.error('Popup bloqueado.');
      // tentar imprimir após carregar
      const timer = setInterval(() => {
        try { win.document && win.focus(); win.print(); clearInterval(timer); } catch { }
      }, 800);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (error) {
      toast.error('Problema na impressão: ' + error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={ui.container}>
      {/* Header */}
      <div style={ui.headerBar}>
        <h1 style={ui.headerTitle}>Controle de Boletos</h1>
        <button onClick={() => navigate('/dashboard')} style={ui.headerButton}>
          <FaHome style={{ marginRight: 8 }} /> Voltar
        </button>
      </div>

      {/* Filtros */}
      <div style={ui.card}>
        <h3 style={ui.sectionTitle}>Filtros</h3>
        <div style={ui.filtersRow}>

          <div style={ui.formGroup}>
            <label style={ui.label}>Vencimento Inicial</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={ui.input} />
          </div>

          <div style={ui.formGroup}>
            <label style={ui.label}>Vencimento Final</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={ui.input} />
          </div>

          <div style={ui.formGroup}>
            <label style={ui.label}>Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={ui.select}>
              <option value="Todas">Todas</option>
              <option value="Aberto">Aberto</option>
              <option value="Gerando">Gerando</option>
              <option value="Gerado">Gerado</option>
              <option value="Pago">Pago</option>
              <option value="Vencido">Vencido</option>
              <option value="Cancelado">Cancelado</option>
            </select>
          </div>

          <div style={ui.formGroup}>
            <label style={ui.label}>Forma Pagto</label>
            <select value={formaFilter} onChange={(e) => setFormaFilter(e.target.value)} style={ui.select}>
              <option value="TODAS">Todas</option>
              <option value="COMPE">Banco</option>
              <option value="MANUAL">MANUAL</option>
              <option value="PIX">PIX</option>
            </select>
          </div>


          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 240, flex: 1 }}>
            <label style={ui.label}>Busca rápida</label>
            <input
              style={ui.input}
              placeholder="Cliente, ID, Nosso Número ou Linha Digitável…"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button onClick={fetchBoletos} disabled={loading || loadingEmitentes} style={ui.buttonPrimary}>
              {loading ? (<><FaSpinner className="fa-spin" style={{ marginRight: 8 }} /> Buscando…</>)
                : (<><FaSearch style={{ marginRight: 8 }} /> Buscar </>)}
            </button>
            <button onClick={() => { setEditingBoleto(null); setShowModal(true); }} style={ui.buttonPrimary}>
              + Incluir
            </button>

          </div>

        </div>
      </div>

      {/* Resumo */}
      <div style={{ ...ui.card, display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12 }}>
        <div style={ui.kpi}><div style={ui.kpiLabel}>Total</div><div style={ui.kpiValue}>{resumo.total}</div></div>
        <div style={ui.kpi}><div style={ui.kpiLabel}>Pagos</div><div style={ui.kpiValue}>{resumo.pagos}</div></div>
        <div style={ui.kpi}><div style={ui.kpiLabel}>Abertos</div><div style={ui.kpiValue}>{resumo.abertos}</div></div>
        <div style={ui.kpi}><div style={ui.kpiLabel}>Vencidos</div><div style={ui.kpiValue}>{resumo.vencidos}</div></div>
        <div style={ui.kpi}><div style={ui.kpiLabel}>Jobs Geração</div><div style={ui.kpiValue}>{jobIds.length}...gerando</div></div>
        <div style={ui.kpi}><div style={ui.kpiLabel}>Soma (R$)</div><div style={ui.kpiValue}>{currencyBRL(resumo.soma)}</div></div>
      </div>

      {/* Ações em massa */}
      <div style={ui.bulkBar}>
        <div style={ui.bulkGroupLeft}>

          {/* <button style={ui.bulkBtnGhost} onClick={toggleSelectAllCurrent} title="Selecionar/Desmarcar os itens da página">
            ☐/☑ Selecionar página
          </button> */}

          <div style={ui.bulkDivider} />

          {/* <button
            style={{ ...ui.bulkBtnPrimary, ...(selectedIds.size === 0 ? ui.btnDisabled : {}) }}
            onClick={bulkMarkPaid}
            disabled={selectedIds.size === 0}
            title="Dar baixa nos boletos selecionados"
          >
            <FaCheckCircle style={ui.iconLeft} /> Dar baixa
          </button> */}


          {/* Reenviar E-mail em massa */}
          {/* <button
            style={{ ...ui.bulkBtn, ...(selectedIds.size === 0 ? ui.btnDisabled : {}) }}
            onClick={() => handleOpenConfirmation('email')}
            disabled={loading || selectedIds.size === 0}
            title="Reenviar por e-mail"
          >
            {loading ? (
              <>
                <FaSpinner className="fa-spin" style={ui.iconLeft} /> Enviando...
              </>
            ) : (
              <>
                <FaEnvelope style={ui.iconLeft} /> Reenviar e-mail
              </>
            )}
          </button> */}

          {/* Reenviar WhatsApp em massa */}
          <button
            style={{ ...ui.bulkBtn, ...(selectedIds.size === 0 ? ui.btnDisabled : {}) }}
            onClick={() => handleOpenConfirmation('whatsapp')}
            disabled={loading || selectedIds.size === 0}
            title="Reenviar por WhatsApp"
          >
            {loading ? (
              <>
                <FaSpinner className="fa-spin" style={ui.iconLeft} /> Enviando...
              </>
            ) : (
              <>
                <FaWhatsapp style={ui.iconLeft} /> Reenviar WhatsApp
              </>
            )}
          </button>

          {/* Imprimir em massa */}
          <button
            style={{ ...ui.bulkBtn, ...(selectedIds.size === 0 ? ui.btnDisabled : {}) }}
            onClick={() => imprimirSelecionados()}
            disabled={loading || selectedIds.size === 0}
            title="Imprimir todos os selecionados"
          >
            {loading ? (
              <>
                <FaSpinner className="fa-spin" style={ui.iconLeft} /> Imprimindo...
              </>
            ) : (
              <>
                <FaFilePdf style={ui.iconLeft} /> Imprimir Selecionados
              </>
            )}
          </button>

          {/* Download pdf em massa */}
          <button
            style={{ ...ui.bulkBtn, ...(selectedIds.size === 0 ? ui.btnDisabled : {}) }}
            onClick={() => downloadSelecionados()}
            disabled={loading || selectedIds.size === 0}
            title="Baixar pdf de todos os selecionados"
          >
            {loading ? (
              <>
                <FaSpinner className="fa-spin" style={ui.iconLeft} /> Baixando ...
              </>
            ) : (
              <>
                <FaDownload style={ui.iconLeft} /> Download PDFS
              </>
            )}
          </button>

          {/* Baixar boletos em massa */}
          <button
            style={{ ...ui.bulkBtn, ...(selectedIds.size === 0 ? ui.btnDisabled : {}) }}
            onClick={() => handleBaixaManualLote()}
            disabled={loading || selectedIds.size === 0}
            title="Dar baixa manual em lote"
          >
            {loading ? (
              <>
                <FaSpinner className="fa-spin" style={ui.iconLeft} /> Baixando ...
              </>
            ) : (
              <>
                <FaDownload style={ui.iconLeft} /> Baixar Boletos
              </>
            )}
          </button>

        </div>

        <div style={ui.bulkGroupRight}>
          <button
            style={{ ...ui.boletoBtn }}
            onClick={() => gerarBoletos('boletos')}
            disabled={selectedIds.size === 0 || jobIds.length > 0}
            title={jobIds.length > 0 ? "Aguarde concluir os jobs ativos" : "Gerar Boletos no Banco"}
          >
            <FaBarcode style={ui.iconLeft} /> Gerar Boletos no Banco
          </button>
        </div>

        <div style={ui.bulkGroupRight}>
          <button
            style={{ ...ui.boletoSinc }}
            onClick={() => handleOpenSincronizar()}
            disabled={jobIds.length > 0}
            title={jobIds.length > 0 ? "Aguarde concluir os jobs ativos" : "Sincronizar baixa com o Banco"}
          >
            <FaCircleNotch style={ui.iconLeft} /> Sincronizar
          </button>
        </div>


        {/* <div style={ui.bulkGroupRight}>
          <span style={ui.bulkCount}>
            Selecionados: <b>{selectedIds.size}</b>
          </span>
        </div> */}

      </div>

      {/* Resultados */}
      <div style={ui.card}>
        {/* <h3 style={ui.sectionTitle}>Resultados</h3> */}
        {/* Cabeçalho + seletor "Imprimir boleto" */}
        <div style={ui.sectionHeaderRow}>
          <h3 style={ui.sectionTitleNoBorder}>Resultados</h3>

          <div style={ui.inlineGroup}>
            <label style={ui.inlineLabel}>Somente clientes que imprime boletos:</label>
            <select
              value={imprimirBoleto}
              onChange={(e) => handleImprimirChange(e.target.value)}
              style={ui.inlineSelect}
            >
              <option value="T">Todos</option>
              <option value="S">Sim</option>
              <option value="N">Não</option>
            </select>
          </div>
        </div>


        {loading && <p style={ui.muted}>Carregando boletos…</p>}
        {!loading && allBoletos.length === 0 && (
          <p style={ui.errorText}>Nenhum boleto encontrado com os filtros selecionados.</p>
        )}

        {!loading && allBoletos.length > 0 && (
          <>
            {/* <div style={{ overflowX: 'auto' }}> */}
            <div style={ui.tableWrap}>
              <table style={ui.table}>
                <thead>
                  <tr>
                    <th style={{ ...ui.th, width: 40 }}>
                      <input
                        type="checkbox"
                        title="Selecionar página"
                        onChange={toggleSelectAllCurrent}
                        checked={paged.length > 0 && paged.every((b) => selectedIds.has(b.id))}
                      />
                    </th>
                    {/* <th style={ui.th} onClick={() => toggleSort('id')}>ID <SortIcon col="id" /></th> */}
                    <th style={ui.th} onClick={() => toggleSort('nome')}>Cliente <SortIcon col="nome" /></th>
                    <th style={ui.th} onClick={() => toggleSort('valor')}>Valor <SortIcon col="valor" /></th>
                    <th style={ui.th} onClick={() => toggleSort('vencimento')}>Vencimento <SortIcon col="vencimento" /></th>
                    <th style={ui.th} onClick={() => toggleSort('status')}>Status <SortIcon col="status" /></th>
                    <th style={ui.th} onClick={() => toggleSort('forma_pagto')}>Forma Pg <SortIcon col="forma_pagto" /></th>
                    <th style={ui.th} onClick={() => toggleSort('pagamento')}>Dt Pagto <SortIcon col="pagamento" /></th>
                    <th style={ui.th}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((boleto) => (
                    <tr key={boleto.id} style={ui.tr}>
                      <td style={{ ...ui.td, textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(boleto.id)}
                          onChange={() => toggleRow(boleto.id)}
                        />
                      </td>
                      {/* <td style={ui.td}>{boleto.id}</td> */}
                      <td style={ui.td}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span>{boleto.nome.substring(0, 50)}</span>
                          {(boleto.nosso_numero || boleto.nossoNumero) ? (
                            <small style={{ color: '#6b7280' }}>
                              NN: {boleto.nosso_numero || boleto.nossoNumero}
                            </small>
                          ) : null}
                        </div>
                      </td>
                      <td style={ui.td}>{currencyBRL(boleto.valor)}</td>
                      <td style={ui.td}>
                        {boleto.vencimento ? format(parseISO(boleto.vencimento), 'dd/MM/yyyy') : 'N/A'}
                      </td>
                      <td style={ui.td}>
                        <span style={statusBadgeStyle(boleto.status)}>{boleto.status || '—'}</span>
                      </td>
                      <td style={ui.td}>
                        <span style={statusBadgeStyle(boleto.forma_pagto)}>{boleto.forma_pagto || '—'}</span>
                      </td>
                      <td style={ui.td}>
                        {boleto.pagamento
                          ? format(parseISO(boleto.pagamento), 'dd/MM/yyyy HH:mm')
                          : 'N/A'}
                      </td>
                      <td style={ui.tdActions}>

                        {boleto.caminho_http_doc && boleto.pdf_gerado === 'S' && (
                          <>
                            <button
                              title="Abrir PDF"
                              onClick={() => handleOpenBoletoPdf(boleto.id, boleto.nome_arquivo, boleto.linhadigitavel)}
                              style={ui.actionPrimary}
                            >
                              <FaFilePdf />
                            </button>
                          </>
                        )}


                        {boleto.status === 'Gerado' && boleto.pdf_gerado === 'S' && (
                          <>

                            <button
                              title="Editar vencimento"
                              onClick={() => { setBoletoToEdit(boleto); setOpenEditModal(true); }}
                              style={ui.actionPrimary}
                            >
                              <FaTools />
                            </button>

                            <button
                              title="Copiar linha QRCODE PIX"
                              onClick={() =>
                                copyToClipboard(
                                  boleto.qrcode,
                                  'QRCODE PIX copiado com sucesso!'
                                )
                              }
                              style={ui.actionSecondary}
                            >
                              <FaCopy />
                            </button>

                            <button
                              title="Reenviar por e-mail"
                              onClick={() => handleResendEmail(boleto.id, boleto.status)}
                              style={{
                                ...ui.actionSecondary,
                                ...(boleto.email_enviado === 'S' && {
                                  backgroundColor: '#28a745', // Verde para indicar enviado
                                  color: 'white'
                                }),
                                ...(boleto.email_enviado === 'N' && {
                                  backgroundColor: '#dc3545', // Vermelho para indicar não enviado
                                  color: 'white'
                                })
                              }}
                            >
                              <FaEnvelope />
                            </button>

                            <button
                              title="Reenviar WhatsApp"
                              onClick={() => handleResendWhatsapp(boleto.id)}
                              style={{
                                ...ui.actionSecondary,
                                ...(boleto.whats_enviado === 'S' && {
                                  backgroundColor: '#25d366', // Verde WhatsApp para indicar enviado
                                  color: 'white'
                                }),
                                ...(boleto.whats_enviado === 'N' && {
                                  backgroundColor: '#dc3545', // Vermelho para indicar não enviado
                                  color: 'white'
                                })
                              }}
                            >
                              <FaWhatsapp />
                            </button>

                            <button
                              title="Dar Baixa"
                              onClick={() => handleBaixaManual(boleto.id)}
                              style={ui.actionSuccess}
                            >
                              <FaCheckCircle />
                            </button>

                          </>
                        )}

                        {/* Editar/Excluir Faturamento */}
                        {boleto.status === 'Aberto' && (
                          <>
                            <button
                              title="Editar"
                              onClick={() => { setEditingBoleto(boleto); setShowModal(true); }}
                              style={ui.actionPrimary}
                            >
                              <FaEdit />
                            </button>
                            <button
                              title="Excluir"
                              onClick={() => { handleDeleteClick(boleto); }}
                              style={ui.actionDelete}
                            >
                              <FaTrash />
                            </button>
                          </>
                        )}

                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div style={ui.paginationContainer}>
                <button onClick={goToPreviousPage} disabled={currentPage === 1} style={ui.paginationNav}>
                  Anterior
                </button>
                {renderPageNumbers()}
                <button onClick={goToNextPage} disabled={currentPage === totalPages} style={ui.paginationNav}>
                  Próxima
                </button>
              </div>
            )}
          </>
        )}


        {showLayoutModal && (
          <div style={{
            position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
            backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center',
            alignItems: 'center', zIndex: 9999
          }}>
            <div style={{
              backgroundColor: '#fff', padding: '25px', borderRadius: '12px',
              width: '400px', boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
            }}>
              <h3 style={{ marginBottom: '15px', color: '#333' }}>Selecione o Layout / Banco</h3>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.9rem', color: '#666' }}>
                  Configuração de Emissão:
                </label>
                <select
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc' }}
                  value={layoutSelecionado}
                  onChange={(e) => setLayoutSelecionado(e.target.value)}
                >
                  {layouts.map(l => (
                    <option key={l.id} value={l.id}>
                      {l.banco === '748' ? 'Sicredi' : l.banco === '341' ? 'Itaú' : `Banco ${l.banco}`} - Ag: {l.agencia} / Ct: {l.conta}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  onClick={() => setShowLayoutModal(false)}
                  style={{ padding: '8px 15px', border: 'none', background: '#ccc', borderRadius: '6px', cursor: 'pointer' }}
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    setShowLayoutModal(false);
                    confirmarGeracaoFinal(); // Função que realmente chama a API
                  }}
                  style={{ padding: '8px 15px', border: 'none', background: '#8b5cf6', color: '#fff', borderRadius: '6px', cursor: 'pointer' }}
                >
                  Confirmar e Gerar
                </button>
              </div>
            </div>
          </div>
        )}

      </div>

      <ModalBoleto
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        initialData={editingBoleto}
        URL_BACKEND={URL_BACKEND}
        token={token}
        emitenteId={selectedEmitenteId}   // <-- novo
        descricaoDefault="Boleto de Honorários ref. ao mês vigente." // opcional
        onSaved={() => { setShowModal(false); fetchBoletos(); }}
      />

      {/* Modal de confirmação */}
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setCurrentAction(""); setSelectedId(0) }}
        // onConfirm={handleConfirm}  //  {handleBulkResend}
        onConfirm={(boletoCancelado) => { handleConfirm(boletoCancelado) }}
        message={modalMessage}//{`Reenviar ${kindToSend === 'email' ? 'e-mails' : 'WhatsApp'} de ${selectedIds.size} boletos?`}
        status={currentAction}
      />

      {/* Modal de Sincronização com o Banco */}
      <ModalSincronizacao
        isOpen={openModalSinc}
        onClose={handleCloseModalSinc}
        onConfirm={handleConfirmSinc}
        message="Deseja sincronizar o banco de dados com as informações do período?"
        startDate={dates.startDate}
        endDate={dates.endDate}
        onDateChange={handleDateChange}
        isLoading={isLoading} // PASSA A NOVA PROP

        // NOVAS PROPS:
        layouts={layouts} // A lista que você já buscou da API
        selectedLayout={layoutSinc}
        onLayoutChange={(id) => setLayoutSinc(id)}
      />

      {/* Modal para alterar somente a data de vencimento */}
      <ModalEditarBoleto
        isOpen={openEditModal}
        onClose={() => setOpenEditModal(false)}
        initialData={boletoToEdit}
        onSave={(data) => handleAlteraVencimento(data)}
      />

    </div>
  );
};

/** 🎨 UI */
const ui = {
  container: {
    padding: 24,
    backgroundColor: '#f8fafc',
    minHeight: '95vh',
    width: '100%',
    maxWidth: '1200px',
    margin: '0 auto',
    boxSizing: 'border-box',
    marginBottom: 40
  },

  headerBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 24,
    marginBottom: 24,
    border: '1px solid #e5e7eb',
    borderRadius: '16px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
    color: '#fff'
  },
  headerTitle: { margin: 0, fontSize: '1.5rem', fontWeight: 600 },
  headerButton: {
    backgroundColor: 'rgba(255,255,255,0.15)',
    color: '#fff',
    padding: '10px 16px',
    borderRadius: 10,
    border: '2px solid rgba(255,255,255,0.25)',
    cursor: 'pointer',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center'
  },

  card: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    border: '1px solid #e5e7eb',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
    padding: 10,
    marginBottom: 24
  },
  sectionTitle: {
    margin: '0 0 16px 0',
    fontSize: '1.125rem',
    fontWeight: 600,
    color: '#1f2937',
    borderBottom: '2px solid #e5e7eb',
    paddingBottom: 8
  },

  filtersRow: { display: 'flex', flexWrap: 'wrap', gap: 5 },
  formGroup: { display: 'flex', flexDirection: 'column', minWidth: 100, flex: 1 },
  label: { marginBottom: 6, fontWeight: 500, color: '#374151', fontSize: '0.875rem' },
  helpText: { fontSize: '0.75rem', color: '#6b7280', marginTop: 4, display: 'flex', alignItems: 'center' },
  input: {
    padding: '12px 10px',
    borderRadius: 8,
    border: '2px solid #e5e7eb',
    fontSize: '0.875rem',
    transition: 'all 0.2s ease',
    backgroundColor: '#ffffff',
    outline: 'none'
  },
  select: {
    padding: '12px 16px',
    borderRadius: 8,
    border: '2px solid #e5e7eb',
    fontSize: '0.875rem',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    outline: 'none',
    transition: 'all 0.2s ease'
  },
  buttonPrimary: {
    backgroundColor: '#667eea',
    color: '#ffffff',
    padding: '12px 16px',
    borderRadius: 8,
    border: '2px solid #667eea',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '0.875rem',
    display: 'flex',
    alignItems: 'center',
    margin: 5
  },

  // KPIs
  kpi: {
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: 12,
    background: '#f9fafb'
  },
  kpiLabel: { fontSize: 12, color: '#6b7280', marginBottom: 6 },
  kpiValue: { fontSize: 18, fontWeight: 700, color: '#111827' },

  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: '0 8px',
    tableLayout: 'auto'   // ⬅️ impede que a tabela “estoure” o width
  },

  th: {
    backgroundColor: '#e9ecef',
    padding: 8,
    textAlign: 'left',
    color: '#555',
    fontWeight: 'bold',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: '0.85rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '2px solid #dee2e6',
    cursor: 'pointer',
    whiteSpace: 'nowrap'
  },

  tr: { backgroundColor: '#fdfdfe', transition: 'background-color 0.2s ease', minHeight: 45 },
  td: {
    padding: 8,
    borderBottom: '1px solid #e9ecef',
    color: '#495057',
    fontSize: '0.8rem',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    lineHeight: 1.3,
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  tdActions: { padding: '6px 8px', borderBottom: '1px solid #e9ecef', display: 'flex', gap: 6, alignItems: 'center' },

  tableWrap: {
    /* altura máxima da área visível da tabela */
    maxHeight: '60vh',         // ajuste como preferir (px ou vh)
    overflowY: 'auto',
    overflowX: 'hidden',   // ⬅️ tira a barra horizontal
    border: '1px solid #e5e7eb',
    borderRadius: 12
  },

  thSticky: {
    backgroundColor: '#e9ecef', // mantém o fundo sólido por cima das linhas
    padding: 8,
    textAlign: 'left',
    color: '#555',
    fontWeight: 'bold',
    fontSize: '0.85rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '2px solid #dee2e6',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    position: 'sticky',
    top: 0,
    zIndex: 2               // garante que fique acima do corpo
  },


  // Badges
  badgeGreen: {
    backgroundColor: '#d1fae5',
    color: '#065f46',
    padding: '4px 10px',
    borderRadius: 999,
    fontWeight: 700,
    fontSize: '0.75rem'
  },
  badgeRed: {
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    padding: '4px 10px',
    borderRadius: 999,
    fontWeight: 700,
    fontSize: '0.75rem'
  },
  badgeYellow: {
    backgroundColor: '#fef3c7',
    color: '#92400e',
    padding: '4px 10px',
    borderRadius: 999,
    fontWeight: 700,
    fontSize: '0.75rem'
  },
  badgeGray: {
    backgroundColor: '#e5e7eb',
    color: '#374151',
    padding: '4px 10px',
    borderRadius: 999,
    fontWeight: 700,
    fontSize: '0.75rem'
  },

  // Ações
  actionPrimary: { backgroundColor: '#3b82f6', color: '#fff', border: 'none', padding: 8, borderRadius: 8, cursor: 'pointer' },
  actionDelete: { backgroundColor: '#c92929', color: '#fff', border: 'none', padding: 8, borderRadius: 8, cursor: 'pointer' },
  actionSecondary: { backgroundColor: '#6b7280', color: '#fff', border: 'none', padding: 8, borderRadius: 8, cursor: 'pointer' },
  actionSuccess: { backgroundColor: '#10b981', color: '#fff', border: 'none', padding: 8, borderRadius: 8, cursor: 'pointer' },

  // Paginação
  paginationContainer: { display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 20, gap: 10 },
  paginationNav: {
    backgroundColor: '#667eea',
    color: '#fff',
    padding: '10px 18px',
    borderRadius: 8,
    border: 'none',
    fontSize: '0.95rem',
    fontWeight: 'bold',
    cursor: 'pointer'
  },
  paginationList: { display: 'flex', listStyle: 'none', margin: 0, padding: 0, gap: 6 },
  paginationItem: { cursor: 'pointer' },
  paginationButton: {
    backgroundColor: '#f0f2f5',
    color: '#333',
    padding: '10px 15px',
    borderRadius: 8,
    border: '1px solid #ddd',
    fontSize: '0.95rem',
    minWidth: 40,
    cursor: 'pointer'
  },
  paginationButtonActive: { backgroundColor: '#667eea', color: '#fff', borderColor: '#667eea' },

  muted: { color: '#6b7280' },
  errorText: { color: '#ef4444', fontWeight: 600 },

  // Ações em massa
  bulkBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    borderRadius: 12,
    border: '1px solid #e5e7eb',
    background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
    boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
    gap: 12,
    marginBottom: 12
  },
  bulkGroupLeft: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  bulkGroupRight: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginLeft: 'auto' },

  iconLeft: { marginRight: 8 },

  bulkBtn: {
    backgroundColor: '#eef2ff',
    color: '#4338ca',
    border: '1px solid #c7d2fe',
    padding: '10px 14px',
    borderRadius: 10,
    cursor: 'pointer',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    transition: 'transform 0.05s ease, box-shadow 0.2s ease'
  },
  boletoBtn: {
    backgroundColor: '#79e99b',
    color: '#4338ca',
    border: '1px solid #c7d2fe',
    padding: '10px 14px',
    borderRadius: 10,
    cursor: 'pointer',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    transition: 'transform 0.05s ease, box-shadow 0.2s ease'
  },
  boletoSinc: {
    backgroundColor: '#53534c',
    color: '#f8f8f8',
    border: '1px solid #c7d2fe',
    padding: '10px 14px',
    borderRadius: 10,
    cursor: 'pointer',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    transition: 'transform 0.05s ease, box-shadow 0.2s ease'
  },
  bulkBtnPrimary: {
    backgroundColor: '#4f46e5',
    color: '#fff',
    border: '1px solid #4338ca',
    padding: '10px 14px',
    borderRadius: 10,
    cursor: 'pointer',
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    boxShadow: '0 2px 8px rgba(79,70,229,0.25)',
    transition: 'transform 0.05s ease, box-shadow 0.2s ease'
  },
  bulkBtnGhost: {
    backgroundColor: '#fff',
    color: '#374151',
    border: '1px solid #e5e7eb',
    padding: '10px 14px',
    borderRadius: 10,
    cursor: 'pointer',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    transition: 'transform 0.05s ease, box-shadow 0.2s ease'
  },
  btnDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
    boxShadow: 'none',
    transform: 'none'
  },

  bulkDivider: {
    width: 1,
    height: 28,
    backgroundColor: '#e5e7eb',
    margin: '0 2px'
  },

  bulkCount: {
    backgroundColor: '#eef2ff',
    color: '#3730a3',
    border: '1px solid #c7d2fe',
    padding: '6px 10px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700
  },
  bulkHint: { fontSize: 12, color: '#6b7280' },

  // Widget
  bgContainer: {
    position: 'fixed',
    right: 20,
    bottom: 20,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 14px',
    background: '#111827',
    color: '#fff',
    borderRadius: 14,
    boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
    border: '1px solid #1f2937',
    zIndex: 9999,
    maxWidth: 320
  },
  bgSpinner: {
    width: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#1f2937',
    borderRadius: 999
  },
  bgText: { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 160 },
  bgTitle: { fontSize: 13, fontWeight: 700 },
  bgSub: { fontSize: 12, color: '#d1d5db' },
  bgBar: {
    height: 6,
    width: '100%',
    background: '#374151',
    borderRadius: 999,
    overflow: 'hidden'
  },
  bgBarFill: { height: '100%', background: '#60a5fa', transition: 'width 300ms ease' },
  bgClose: {
    marginLeft: 4,
    width: 28,
    height: 28,
    borderRadius: 8,
    border: '1px solid #374151',
    background: '#1f2937',
    color: '#9ca3af',
    cursor: 'pointer',
    fontSize: 16,
    lineHeight: 1
  },
  sectionHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '2px solid #e5e7eb',
    paddingBottom: 8,
    marginBottom: 16
  },
  sectionTitleNoBorder: {
    margin: 0,
    fontSize: '1.125rem',
    fontWeight: 600,
    color: '#1f2937'
  },
  inlineGroup: { display: 'flex', alignItems: 'center', gap: 8 },
  inlineLabel: { fontSize: '0.875rem', color: '#374151', fontWeight: 500 },
  inlineSelect: {
    padding: '8px 12px',
    borderRadius: 8,
    border: '2px solid #e5e7eb',
    fontSize: '0.875rem',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    outline: 'none'
  }

};

export default BoletoControlScreen;
